import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  getEmailConfig,
  listCompanyEmailDomains,
  createCompanyEmailDomain,
  syncEmailDomain,
  disconnectEmailDomain,
} from '../../services/emailDomainService';
import EmailSetupChoice from './EmailSetupChoice';
import DnsRecordsList from './DnsRecordsList';

/**
 * Bloque «Correo electrónico» del perfil de una company (canal de AR, Resend).
 *
 * Tres estados, que son el journey entero:
 *   1. sin dominio  → elegir vía (gestionada por ClaimUp o dominio propio)
 *   2. sin verificar → los registros DNS a publicar, con su estado uno a uno
 *   3. verificado   → desde qué dirección sale el correo
 *
 * Va dentro del bloque «Comunicación» (`CompanyPhonesBlock`, con `embedded`),
 * junto a WhatsApp y llamadas: es un canal más. Se monta solo para admin o el
 * dueño de la company, que es la misma regla que aplica el backend
 * (`canManageCompanyEmail`). Un gestor manda
 * correos en AR pero no conecta dominios: conectar uno cambia la identidad con
 * la que la empresa se dirige a sus deudores.
 */
const CompanyEmailBlock = ({ userData, isAdmin = false, embedded = false }) => {
  const companyId = userData?._id;
  const companyName = [userData?.username, userData?.userlastname].filter(Boolean).join(' ');

  const [config, setConfig] = useState(null);
  const [domains, setDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const [cfg, list] = await Promise.all([getEmailConfig(), listCompanyEmailDomains(companyId)]);
      setConfig(cfg);
      setDomains(list || []);
    } catch (err) {
      console.error('Error cargando el correo de la company:', err);
      setDomains([]);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async payload => {
    setIsSaving(true);
    try {
      await createCompanyEmailDomain(companyId, payload);
      toast.success('Dominio conectado. Ahora hay que verificarlo.');
      await load();
    } catch (err) {
      // El backend distingue el fallo del proveedor del error de quien llama, y
      // manda el motivo en castellano: se enseña tal cual en vez de un genérico.
      toast.error(err.response?.data?.error?.message || 'No se pudo conectar el dominio');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCheck = async domainId => {
    setIsChecking(true);
    try {
      const updated = await syncEmailDomain(domainId, { force: true });
      setDomains(prev => prev.map(d => (d._id === updated._id ? updated : d)));
      if (updated.status === 'verified') {
        toast.success('Dominio verificado. Ya puedes enviar correos.');
      } else {
        toast.info('Todavía no está verificado. La propagación del DNS puede tardar.');
      }
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'No se pudo comprobar el dominio');
    } finally {
      setIsChecking(false);
    }
  };

  /**
   * Cancela un alta a medio hacer y devuelve al selector.
   *
   * Solo aparece **antes de verificar**. Una vez el dominio envía, tiene hilos
   * abiertos y el `Reply-To` de cada correo apunta a él: deshacerlo perdería las
   * respuestas en vuelo, y eso no es un botón de una pantalla de perfil. El
   * backend lo rechaza igualmente con un 409, así que esto es la primera puerta,
   * no la única.
   */
  const handleDisconnect = async domainId => {
    if (
      !window.confirm(
        'Vas a cancelar esta configuración y volver a empezar.\n\n' +
          'Si ya has publicado algún registro DNS, puedes quitarlo: dejará de servir para nada.'
      )
    ) {
      return;
    }

    setIsDisconnecting(true);
    try {
      await disconnectEmailDomain(domainId);
      toast.success('Configuración cancelada. Puedes elegir otra opción.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'No se pudo cancelar');
    } finally {
      setIsDisconnecting(false);
    }
  };

  /** El botón es el mismo en los dos estados de espera, así que vive en un sitio. */
  const botonCancelar = domainId => (
    <button
      type='button'
      onClick={() => handleDisconnect(domainId)}
      disabled={isDisconnecting}
      className='text-sm text-slate-500 hover:text-red-600 disabled:text-slate-300'
    >
      {isDisconnecting ? 'Cancelando…' : 'Cancelar y elegir otra opción'}
    </button>
  );

  const domain = domains[0] || null;

  // Dentro de «Comunicación» es una sección más, con el mismo encabezado
  // pequeño que WhatsApp; suelto, su propia tarjeta.
  return (
    <div className={embedded ? '' : 'bg-white rounded-lg border border-slate-200 overflow-hidden'}>
      {embedded ? (
        <div className='flex items-center mb-2'>
          <EnvelopeIcon className='h-4 w-4 text-slate-400 mr-1.5' />
          <span className='text-sm font-medium text-slate-600'>Correo electrónico</span>
        </div>
      ) : (
        <div className='px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center'>
          <EnvelopeIcon className='h-5 w-5 text-blue-600 mr-2' />
          <h3 className='text-lg font-medium text-slate-900'>Correo electrónico</h3>
        </div>
      )}

      <div className={embedded ? '' : 'p-6'}>
        {isLoading ? (
          <div className='flex justify-center py-4'>
            <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500'></div>
          </div>
        ) : !domain ? (
          <EmailSetupChoice
            companyName={companyName}
            managedDomain={config?.managed_domain}
            managedAvailable={!!config?.managed_available}
            onSubmit={handleCreate}
            isSaving={isSaving}
          />
        ) : domain.status === 'verified' ? (
          <div className='space-y-3'>
            <div className='flex items-start gap-2'>
              <CheckCircleIcon className='w-5 h-5 text-green-600 shrink-0 mt-0.5' />
              <div>
                <p className='text-sm font-medium text-slate-900'>Correo conectado</p>
                <p className='text-sm text-slate-600'>
                  Los correos saldrán desde{' '}
                  <span className='font-mono text-slate-800'>
                    {domain.display_name} &lt;{domain.from_address}&gt;
                  </span>
                </p>
              </div>
            </div>
            <p className='text-xs text-slate-500'>
              Las respuestas de tus deudores entran solas en su expediente. Los primeros días el volumen sale limitado a
              propósito, mientras el dominio gana reputación.
            </p>
          </div>
        ) : domain.mode === 'managed' && !isAdmin ? (
          /* Vía gestionada, visto por el CLIENTE.
             Los registros van en la zona DNS de ClaimUp, no en la suya: no puede
             hacer nada con ellos y enseñárselos solo genera dudas. Ve un
             progreso y la dirección que tendrá, que es lo único accionable
             para él — que es «esperar». */
          <div className='space-y-3'>
            <div className='flex items-start gap-2'>
              <ClockIcon className='w-5 h-5 text-blue-600 shrink-0 mt-0.5' />
              <div>
                <p className='text-sm font-medium text-slate-900'>Lo estamos preparando</p>
                <p className='text-sm text-slate-600'>
                  Nos encargamos nosotros de la configuración técnica.{' '}
                  <span className='font-medium'>No tienes que hacer nada.</span> Suele estar listo en unos minutos.
                </p>
              </div>
            </div>

            <div className='bg-slate-50 border border-slate-200 rounded-lg px-3 py-2'>
              <p className='text-xs text-slate-500 mb-0.5'>Cuando esté listo, saldrá desde:</p>
              <p className='text-sm font-mono text-slate-800 break-all'>
                {domain.display_name} &lt;{domain.from_address}&gt;
              </p>
            </div>

            <button
              type='button'
              onClick={() => handleCheck(domain._id)}
              disabled={isChecking}
              className='inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 disabled:text-slate-400'
            >
              <ArrowPathIcon className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
              {isChecking ? 'Comprobando…' : 'Actualizar estado'}
            </button>

            <div className='pt-2 border-t border-slate-100'>{botonCancelar(domain._id)}</div>
          </div>
        ) : (
          <div className='space-y-4'>
            <div className='flex items-start gap-2'>
              <ExclamationTriangleIcon className='w-5 h-5 text-amber-600 shrink-0 mt-0.5' />
              <div>
                {domain.mode === 'managed' ? (
                  <>
                    <p className='text-sm font-medium text-slate-900'>
                      Falta publicar los registros — los pone ClaimUp
                    </p>
                    <p className='text-sm text-slate-600'>
                      Vía gestionada: estos registros van en <span className='font-medium'>nuestra zona DNS</span>, no
                      en la del cliente. <span className='font-medium'>El cliente no los ve</span>: en su pantalla solo
                      aparece que lo estamos preparando.
                    </p>
                  </>
                ) : (
                  <>
                    <p className='text-sm font-medium text-slate-900'>Falta verificar el dominio</p>
                    <p className='text-sm text-slate-600'>
                      Añade estos registros en el DNS de <span className='font-mono'>{domain.domain}</span>. Suele
                      tardar unos quince minutos en propagarse.
                    </p>
                  </>
                )}
              </div>
            </div>

            <DnsRecordsList records={domain.records} />

            <button
              type='button'
              onClick={() => handleCheck(domain._id)}
              disabled={isChecking}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium ${
                isChecking ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <ArrowPathIcon className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
              {isChecking ? 'Comprobando…' : 'Comprobar ahora'}
            </button>

            <div className='pt-2 border-t border-slate-100'>
              {botonCancelar(domain._id)}
              <p className='text-xs text-slate-400 mt-1'>
                ¿Te has equivocado de opción o de dominio? Cancela y vuelve a empezar.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyEmailBlock;
